/*
 * Tournaments list, the entry point for the team match-play engine
 * on web. Mirrors apps/mobile/src/screens/tournaments/TournamentsListScreen.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Trophy, ChevronRight } from 'lucide-react';

import { api } from '@/lib/api';
import { useTournamentStore } from '@/store/tournamentStore';
import { AppShell } from '@/components/AppShell';
import { EventLogo } from '@/components/EventLogo';
import { StatusPill, formatDateRange } from './_shared';

export default function TournamentsList() {
  const { events, setEvents } = useTournamentStore();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchEvents = useCallback(async () => {
    setError(null);
    try {
      const list = await api.getTournaments();
      setEvents(list);
    } catch (e: any) {
      setError(e?.message ?? "Couldn't load tournaments");
    } finally {
      setLoading(false);
    }
  }, [setEvents]);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  const active = events.filter((e) => e.status !== 'archived');
  const archived = events.filter((e) => e.status === 'archived');

  return (
    <AppShell back={{ to: '/app/home', label: 'Home' }}>
      <section className="t-hero">
        <div className="t-hero-row">
          <div>
            <h1>Tournaments</h1>
            <p>Two-team match play</p>
          </div>
          <Link to="/app/tournaments/new" className="t-btn t-btn-primary">
            <Plus /> Create Cup
          </Link>
        </div>
      </section>

      {error && <div className="t-error">{error}</div>}

      {loading ? (
        <div className="t-loading"><div className="spinner" /></div>
      ) : events.length === 0 ? (
        <div className="t-empty">
          <div className="t-empty-icon"><Trophy /></div>
          <div className="t-empty-title">No tournaments yet</div>
          <div className="t-empty-hint">
            Create your first team match-play event to get started.
          </div>
        </div>
      ) : (
        <>
          <div className="t-list">
            {active.map((e) => (
              <Link key={e.id} to={`/app/tournaments/${e.id}`} className="t-row">
                {e.logoUrl
                  ? <EventLogo logoUrl={e.logoUrl} name={e.name} size={44} />
                  : <div className="t-row-icon"><Trophy /></div>
                }
                <div className="t-row-body">
                  <div className="t-row-title-line">
                    <div className="t-row-name">{e.name}</div>
                    <StatusPill status={e.status} />
                  </div>
                  <div className="t-row-meta">
                    {[e.locationName, formatDateRange(e.startDate, e.endDate)]
                      .filter(Boolean)
                      .join('  ·  ') || 'No dates set'}
                  </div>
                  <div className="t-row-sub">
                    {e.teamCount} team{e.teamCount !== 1 ? 's' : ''}  ·  {e.playerCount} player{e.playerCount !== 1 ? 's' : ''}
                  </div>
                </div>
                <div className="t-row-chevron"><ChevronRight /></div>
              </Link>
            ))}
          </div>

          {archived.length > 0 && (
            <>
              <div className="t-section-label">Archived</div>
              <div className="t-list">
                {archived.map((e) => (
                  <Link key={e.id} to={`/app/tournaments/${e.id}`} className="t-row">
                    {e.logoUrl
                      ? <EventLogo logoUrl={e.logoUrl} name={e.name} size={44} />
                      : <div className="t-row-icon"><Trophy /></div>
                    }
                    <div className="t-row-body">
                      <div className="t-row-title-line">
                        <div className="t-row-name">{e.name}</div>
                        <StatusPill status={e.status} />
                      </div>
                      <div className="t-row-meta">
                        {[e.locationName, formatDateRange(e.startDate, e.endDate)]
                          .filter(Boolean)
                          .join('  ·  ') || 'No dates set'}
                      </div>
                    </div>
                    <div className="t-row-chevron"><ChevronRight /></div>
                  </Link>
                ))}
              </div>
            </>
          )}
        </>
      )}
    </AppShell>
  );
}
